/**
 * Hybrid Icon component.
 * Automatically chooses between the Tailwind (pure CSS-class) render path and the
 * styled-components path based on props, preserving 100% backward compatibility:
 *
 *  - `color` tokens (e.g. "icon.base"), styled-system props (margins, layout),
 *    a styled() extension (theme/forwardedComponent), or an injected `sc-*`
 *    className all route to the styled-components Container.
 *  - bare icons (name + size + fixedWidth, no system props) render via the
 *    lighter Tailwind <span>.
 *
 * Name/variant resolution, viewBox lookup, aria handling, and the <svg> markup
 * are shared with the styled-components path via iconShared.tsx.
 */
import * as React from "react";
import PartnerLogo from "@sproutsocial/seeds-react-partner-logo";
import { includes } from "@sproutsocial/seeds-react-utilities";
import { LogoNamesWithoutVariants as PartnerLogoNames } from "@sproutsocial/seeds-partner-logos";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";

import type { TypeIconProps } from "./IconTypes";
import Container from "./styles";
import { IconTailwind } from "./IconTailwind";
import { ensureGradientDefs } from "./gradientDefs";
import {
  AllViewboxes,
  resolveIconName,
  getAriaProps,
  IconSvg,
} from "./iconShared";

// Matches the generated styled-components class (e.g. "sc-bdVaJa"). A styled(Icon)
// extension injects such a class; routing it to the styled path keeps its
// generated styles (and avoids leaking `theme`/custom props onto the DOM span).
const SC_CLASS_PATTERN = /\bsc-[a-zA-Z0-9]+\b/;

// Any `.seeds-icon-gradient-*` class resolves to `fill: url(#...)`, which needs the
// matching paint server present in the document. Matched here so apps that never use
// a gradient icon don't get the defs element appended to their <body>.
const GRADIENT_CLASS_PATTERN = /\bseeds-icon-gradient-[a-z0-9-]+\b/;

const IconHybrid = ({
  name,
  size = "small",
  fixedWidth = false,
  ariaLabel,
  color,
  svgProps,
  ...rest
}: TypeIconProps) => {
  const wantsGradient =
    typeof rest.className === "string" &&
    GRADIENT_CLASS_PATTERN.test(rest.className);

  // Must run before the PartnerLogo early return to keep hook order stable.
  React.useEffect(() => {
    if (wantsGradient) ensureGradientDefs();
  }, [wantsGradient]);

  if (includes(PartnerLogoNames, name)) {
    // Icon's "default" size is equivalent to PartnerLogo's "small" size
    const logoSize = size === "default" ? "small" : size;
    const logoProps = {
      partnerName: name,
      size: logoSize,
      logoType: "symbol" as const,
      svgProps,
    };
    return (
      <PartnerLogo
        // ariaLabel needs to be overridable by aria-label prop in ...rest
        aria-label={ariaLabel}
        {...rest}
        {...logoProps}
      />
    );
  }

  const iconName = resolveIconName(name, size);
  const iconViewBox = AllViewboxes[iconName];

  const { "aria-label": ariaLabelFromRest, ...restWithoutAriaLabel } =
    rest as Record<string, any>;
  const { containerAriaProps, svgAriaProps } = getAriaProps(
    ariaLabel,
    ariaLabelFromRest
  );

  const svg = (
    <IconSvg
      iconName={iconName}
      iconViewBox={iconViewBox}
      svgProps={svgProps}
      svgAriaProps={svgAriaProps}
    />
  );

  // Route to styled-components whenever backward compatibility requires it.
  // `color` is destructured out of `rest`, so it must be checked explicitly —
  // the Tailwind path only does `fill: currentColor` and cannot resolve a token
  // like "icon.base".
  // `cursor` is a public Icon prop applied by the legacy Container via the
  // COMMON mixin, but is not in STYLED_SYSTEM_PROPS, so `needsStyledComponents`
  // does not catch it. Gate it explicitly (like `color`) so it keeps routing to
  // SC instead of leaking onto the Tailwind span as an invalid DOM attribute.
  // It is read (not destructured) off `restWithoutAriaLabel` so it still spreads
  // onto Container below.
  const className = restWithoutAriaLabel.className;
  const needsSC =
    color !== undefined ||
    restWithoutAriaLabel.cursor !== undefined ||
    needsStyledComponents(restWithoutAriaLabel) ||
    (typeof className === "string" && SC_CLASS_PATTERN.test(className));

  if (needsSC) {
    return (
      <Container
        iconSize={size}
        fixedWidth={!!fixedWidth}
        key={iconName}
        data-qa-icon={iconName}
        color={color as React.ComponentProps<typeof Container>["color"]}
        {...restWithoutAriaLabel}
        {...containerAriaProps}
      >
        {svg}
      </Container>
    );
  }

  return (
    <IconTailwind
      size={size}
      fixedWidth={!!fixedWidth}
      key={iconName}
      data-qa-icon={iconName}
      {...restWithoutAriaLabel}
      {...containerAriaProps}
    >
      {svg}
    </IconTailwind>
  );
};

IconHybrid.displayName = "Icon";
export default IconHybrid;
